import TextField from '@mui/material/TextField'
import MobileWarningDialog from 'components/App/components/MobileWarningDialog'
import ConnectedSnackbar from 'components/ConnectedSnackbar'
import EmailLoginForm from 'components/EmailLoginForm'
import Logo from 'components/PPLogo'
import Loader from 'components/SimpleLoader'
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useLocation, useNavigate, useSearchParams } from 'react-router'
import { ERROR_NO_EMAIL_RETURNED } from 'services/auth/identities/facebook/legacy'
import { isMobileScreen } from 'utils/browser'
import FBLoginButton from '../../components/LoginButton/FBLoginButton'
import { RandomUserQuote } from '../../components/UserQuotePanel/UserQuotePanel'
import styles from './Landing.pcss'

interface LandingState {
  error?: string
  emailMissing: boolean
  email?: string
}

export function Landing() {
  const intl = useIntl()
  const navigate = useNavigate()
  const location = useLocation()
  // NOTE: When ott is present, we show only a loader while waiting for the user to be authenticated
  // On successful login, user is redirected to the app, on error - show the login page with an error message
  const [searchParams] = useSearchParams()
  const loginParams = useMemo(() => Object.fromEntries(searchParams), [searchParams])
  const [isMobile, setIsMobile] = useState(isMobileScreen())
  const [state, setState] = useState<LandingState>({ emailMissing: false })
  const [loading, setLoading] = useState(Boolean(searchParams.get('ott')))

  useEffect(() => {
    const onResize = () => {
      setIsMobile(isMobileScreen())
    }

    window.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('resize', onResize)
    }
  }, [])

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setState(prevState => ({ ...prevState, email: value }))
  }

  useEffect(() => {
    if (state.email) {
      navigate(`${location.pathname}?email=${state.email}`, { replace: true })
    }
  }, [navigate, location.pathname, state.email])

  const onLoginClick = useCallback(() => {
    setState(prev => ({ ...prev, error: undefined, emailMissing: false }))
  }, [])

  const onFBLoginError = useCallback((error: Error & { code?: string, data?: any }) => {
    console.log('[FB login error]', error)
    if (error.code === 'email-not-verified') {
      navigate('/signup/verify', { state: { ott: error.data?.loginOtt } })
      return
    }

    if (error.code === ERROR_NO_EMAIL_RETURNED) {
      setState(prev => ({ ...prev, error: undefined, emailMissing: true }))
      return
    }

    setState(prev => ({
      ...prev,
      error: intl.formatMessage({ id: 'errors.login.with-text' }, { message: error.message }),
      emailMissing: false
    }))
  }, [navigate, intl])

  const onLoginError = () => {
    setLoading(false)
  }

  return (
    <Fragment>
      <section data-test="landing" className={`${styles.wrapper} ${loading ? styles.hidden : ''}`}>
        {isMobile && <MobileWarningDialog />}
        <section className={styles.sidebar}>
          <div className={styles['side-content']}>
            <RandomUserQuote />
          </div>
        </section>
        <div className={styles.main}>
          <Logo className={styles.logo} key="logo" />
          <h1 className={styles.title}>Post Planner</h1>

          <EmailLoginForm
            loginParams={loginParams}
            onLoginError={onLoginError}
            fbLoginSection={(
              <section className={styles.actions}>
                <div className={styles['fb-box']}>
                  <span>
                    <FBLoginButton
                      key="action-facebook"
                      loginParams={loginParams}
                      className={styles['btn-fb-login']}
                      onClick={onLoginClick}
                      onError={onFBLoginError}
                    />
                  </span>
                </div>
                {state.emailMissing && (
                  <div className={styles['email-wrapper']}>
                    <p className={styles.error}>We couldn't get your email. Please enter it below and try logging in again.</p>
                    <TextField
                      type="email"
                      placeholder="Email"
                      classes={{ root: styles.email }}
                      onChange={handleEmailChange}
                    />
                  </div>
                )}
                {state.error && (
                  <p className={styles.error}>
                    {state.error}
                  </p>
                )}
              </section>
            )}
          />
        </div>
        <ConnectedSnackbar />
      </section>
      {loading && <div className={styles.loader}><Loader /></div>}
    </Fragment>
  )
}

export default Landing
